import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { usePublicCopy } from '@/i18n/publicCopy';

export const SAHI_ORB = 'https://static.prod-images.emergentagent.com/jobs/f1259ba7-2352-4ebf-be05-9a668fa2dab4/images/c71c9c5c0753b80240ae1156db14a920ffdd8fa2daf776a12d281e5e940b0ece.jpeg';

export function LangToggle({ lang, setLang, label, compact = false }) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-full p-0.5" style={{ border: '1px solid var(--border-hover)' }} data-testid="public-lang-toggle">
      {['en', 'es'].map((l) => (
        <button key={l} type="button" role="radio" aria-checked={lang === l} onClick={() => setLang(l)}
          className={`${compact ? 'px-2.5' : 'px-3'} py-1 rounded-full text-xs font-medium transition-colors`}
          style={{ backgroundColor: lang === l ? 'var(--accent)' : 'transparent', color: lang === l ? '#fff' : 'var(--text-secondary)' }}
          data-testid={`public-lang-${l}`}>{l.toUpperCase()}</button>
      ))}
    </div>
  );
}

/** Signed-out header for the public pages: logo · How it works · Memberships · Dating · Blog · Clips · EN/ES · Sign in · Start free. */
export function PublicHeader() {
  const { c, lang, setLang } = usePublicCopy();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const h = c.header;
  const links = [
    { to: '/#how-it-works', label: h.how, id: 'how', isHash: true },
    { to: '/pricing', label: h.memberships, id: 'memberships' },
    { to: '/dating', label: h.dating, id: 'dating' },
    { to: '/blog', label: h.blog, id: 'blog' },
    { to: '/clips', label: h.clips, id: 'clips' },
  ];
  const linkStyle = ({ isActive }) => ({ color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)' });
  const start = () => navigate(user ? '/dashboard' : '/values');

  return (
    <header className="sticky top-0 z-50 backdrop-blur-2xl" style={{ backgroundColor: 'rgba(250,245,236,0.9)', borderBottom: '1px solid var(--border)' }} data-testid="public-header">
      <nav aria-label="Primary" className="max-w-6xl mx-auto flex items-center justify-between gap-4 px-5 md:px-8 py-3.5">
        <Link to="/" className="flex items-center gap-2.5" aria-label="Sahiri Amali home" data-testid="public-logo">
          <img src={SAHI_ORB} alt="" className="w-8 h-8 rounded-full" aria-hidden="true" />
          <span className="font-heading text-lg font-semibold tracking-tight" style={{ color: 'var(--text-primary)' }}>Sahiri Amali</span>
        </Link>
        <div className="hidden md:flex items-center gap-6 text-sm">
          {links.map((l) => l.isHash
            ? <a key={l.id} href={l.to} className="hover:opacity-80" style={{ color: 'var(--text-secondary)' }} data-testid={`public-nav-${l.id}`}>{l.label}</a>
            : <NavLink key={l.id} to={l.to} style={linkStyle} className="hover:opacity-80" data-testid={`public-nav-${l.id}`}>{l.label}</NavLink>)}
        </div>
        <div className="flex items-center gap-2.5">
          <LangToggle lang={lang} setLang={setLang} label={h.lang} compact />
          {!user && <button type="button" onClick={() => navigate('/auth')} className="hidden sm:inline-flex px-3.5 py-2 rounded-full text-xs font-medium" style={{ border: '1px solid var(--border-hover)', color: 'var(--text-secondary)' }} data-testid="public-signin">{h.signin}</button>}
          <button type="button" onClick={start} className="px-4 py-2 rounded-full text-xs font-medium text-white transition-transform hover:scale-[1.02]" style={{ backgroundColor: 'var(--accent)' }} data-testid="public-start">{user ? h.dashboard : h.start}</button>
          <button type="button" onClick={() => setOpen((v) => !v)} className="md:hidden p-2 rounded-full" style={{ color: 'var(--text-primary)' }} aria-expanded={open} aria-label={h.menu} data-testid="public-menu-toggle">
            {open ? <X className="w-5 h-5" aria-hidden="true" /> : <Menu className="w-5 h-5" aria-hidden="true" />}
          </button>
        </div>
      </nav>
      {open && (
        <div className="md:hidden px-5 pb-4 flex flex-col gap-3 text-sm" data-testid="public-mobile-menu">
          {links.map((l) => <a key={l.id} href={l.to} onClick={() => setOpen(false)} style={{ color: 'var(--text-primary)' }} data-testid={`public-mobile-nav-${l.id}`}>{l.label}</a>)}
          {!user && <Link to="/auth" onClick={() => setOpen(false)} style={{ color: 'var(--text-secondary)' }} data-testid="public-mobile-signin">{h.signin}</Link>}
        </div>
      )}
    </header>
  );
}
